import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { inr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { LeadDialog } from "@/components/LeadDialog";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Download, Plus, X } from "lucide-react";

const money = (n) => (n ? inr(n) : "₹0.00");

// editable text cell
const T = ({ value, onChange, className = "", ...p }) => (
  <input value={value} onChange={(e) => onChange(e.target.value)} className={`w-full bg-transparent outline-none ${className}`} {...p} />
);
const N = ({ value, onChange, className = "" }) => (
  <input type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} className={`w-full bg-transparent text-right outline-none ${className}`} />
);

const defBasic = [
  { desc: "Super Built Up Area", unit: "SQFT", qty: 1837, rate: 3000 },
  { desc: "Service Area (SQ)", unit: "SQFT", qty: 53, rate: 3000 },
  { desc: "Private Terrace", unit: "SQFT", qty: 0, rate: 0 },
  { desc: "PLC", unit: "SQFT", qty: 0, rate: 0 },
  { desc: "Highrise Price", unit: "SQFT", qty: 0, rate: 0 },
  { desc: "Car Parking", unit: "NO", qty: 1, rate: 400000 },
];
const defExtras = [
  { desc: "Infrastructure Cost", unit: "SQFT", qty: 1837, rate: 0 },
  { desc: "Club Charges", unit: "SQFT", qty: 0, rate: 0 },
  { desc: "Documentation Charges", unit: "ONE TIME", qty: 1, rate: 25000 },
];

export default function CostSheetPage() {
  const [cust, setCust] = useState({
    app1: "", pan1: "", app2: "", pan2: "", contact1: "", contact2: "", email: "", address: "",
    category: "", flatNo: "", floor: "", block: "", wing: "", sbua: 0, bua: 0, carpet: 0, balcony: 0,
    highrise: "N/A", plc: "N/A", parking: "",
  });
  const [basic, setBasic] = useState(defBasic);
  const [extras, setExtras] = useState(defExtras);
  const [maint, setMaint] = useState({ qty: 1890, rate: 3, months: 24 });
  const [propGst, setPropGst] = useState(5); // 1 or 5
  const setC = (k, v) => setCust({ ...cust, [k]: v });

  const upd = (list, setList) => (i, k, v) => setList(list.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addRow = (list, setList) => () => setList([...list, { desc: "New item", unit: "SQFT", qty: 0, rate: 0 }]);
  const delRow = (list, setList) => (i) => setList(list.filter((_, idx) => idx !== i));

  const calc = useMemo(() => {
    const lineTot = (r) => r.qty * r.rate;
    const sum = (list) => list.reduce((a, r) => a + lineTot(r), 0);
    const basicTotal = sum(basic), extrasTotal = sum(extras);
    const maintTotal = maint.qty * maint.rate * maint.months;
    const basicGst = basicTotal * propGst / 100;
    const extrasGst = extrasTotal * propGst / 100;
    const maintGst = maintTotal * 0.18;
    const totalCost = basicTotal + extrasTotal + maintTotal;
    const totalGst = basicGst + extrasGst + maintGst;
    return { lineTot, basicTotal, extrasTotal, maintTotal, basicGst, extrasGst, maintGst, totalCost, totalGst, grand: totalCost + totalGst };
  }, [basic, extras, maint, propGst]);

  const th = "border border-neutral-400 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide";
  const td = "border border-neutral-400 px-2 py-1.5 text-sm";
  const secBar = "border border-neutral-400 bg-neutral-900 px-3 py-2 text-center text-sm font-extrabold uppercase tracking-widest text-white";

  const rowBlock = (list, setList, gstLabel) => list.map((r, i) => {
    const tot = calc.lineTot(r);
    return (
      <tr key={i} className="group">
        <td className={`${td} relative`}>
          <T value={r.desc} onChange={(v) => upd(list, setList)(i, "desc", v)} />
          <button onClick={() => delRow(list, setList)(i)} className="no-print absolute -left-6 top-1/2 hidden -translate-y-1/2 text-destructive group-hover:block"><X className="h-4 w-4" /></button>
        </td>
        <td className={`${td} text-center`}><T value={r.unit} onChange={(v) => upd(list, setList)(i, "unit", v)} className="text-center" /></td>
        <td className={`${td}`}><N value={r.qty} onChange={(v) => upd(list, setList)(i, "qty", v)} /></td>
        <td className={`${td} font-mono`}><N value={r.rate} onChange={(v) => upd(list, setList)(i, "rate", v)} /></td>
        <td className={`${td} text-right font-mono`}>{money(tot)}</td>
        <td className={`${td} text-right font-mono`}>{money(tot * propGst / 100)}</td>
      </tr>
    );
  });

  return (
    <div className="bg-background pt-28">
      <div className="container-px mx-auto max-w-[960px] pb-24">
        <div className="no-print mb-8 flex flex-wrap items-center justify-between gap-4">
          <Link to="/tools" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Back to tools
          </Link>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 border border-border px-3 py-2">
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Property GST</span>
              {[1, 5].map((g) => (
                <button key={g} onClick={() => setPropGst(g)} data-testid={`cs-gst-${g}`}
                  className={`px-2 py-1 font-mono text-xs ${propGst === g ? "bg-cyan text-white" : "text-foreground"}`}>{g}%</button>
              ))}
            </div>
            <LeadDialog source="detailed-cost-sheet" defaultNote="Please share my detailed cost sheet / booking estimate."
              extraData={() => ({ customer: cust, basic, extras, maintenance: maint, prop_gst: propGst, totals: {
                basicTotal: calc.basicTotal, basicGst: calc.basicGst,
                extrasTotal: calc.extrasTotal, extrasGst: calc.extrasGst,
                maintTotal: calc.maintTotal, maintGst: calc.maintGst,
                totalCost: calc.totalCost, totalGst: calc.totalGst, grand: calc.grand,
              } })}
              onSuccess={async (payload) => {
                try {
                  toast.loading("Preparing your PDF…", { id: "cs-pdf" });
                  const res = await fetch(`${API}/cost-sheet/pdf`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                  });
                  if (!res.ok) throw new Error("PDF generation failed");
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  const stamp = new Date().toISOString().slice(0, 10);
                  a.download = `cost-sheet-${stamp}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                  toast.success("Your PDF has been downloaded.", { id: "cs-pdf" });
                } catch (e) {
                  toast.error("Couldn't build the PDF. Opening the print dialog instead.", { id: "cs-pdf" });
                  window.print();
                }
              }}
              trigger={
                <Button className="rounded-none bg-cyan font-mono text-xs uppercase tracking-widest text-white hover:bg-cyan/90" data-testid="cs-download">
                  <Download className="mr-2 h-4 w-4" /> Download PDF
                </Button>
              } />
          </div>
        </div>

        {/* ================= DOCUMENT ================= */}
        <div id="cost-sheet-doc" className="border border-neutral-400 bg-white p-5 text-neutral-900 md:p-8">
          <div className="mb-1 text-center text-xl font-extrabold uppercase tracking-widest">Booking Cost Sheet</div>
          <div className="mb-4 text-right text-xs font-bold">DATE: {new Date().toLocaleDateString("en-GB")}</div>

          {/* Customer + address */}
          <table className="w-full border-collapse">
            <tbody>
              <tr>
                <td className={`${th} bg-neutral-100 text-center`} colSpan={2}>Customer Details</td>
                <td className={`${th} bg-neutral-100 text-center`} colSpan={2}>Permanent Address</td>
              </tr>
              <tr>
                <td className={`${td} w-[22%] font-semibold`}>First Applicant</td>
                <td className={td}><T value={cust.app1} onChange={(v) => setC("app1", v)} placeholder="Mr. ..." /></td>
                <td className={`${td}`} colSpan={2} rowSpan={4}><T value={cust.address} onChange={(v) => setC("address", v)} placeholder="Permanent address..." className="h-full" /></td>
              </tr>
              <tr><td className={`${td} font-semibold`}>PAN No.</td><td className={td}><T value={cust.pan1} onChange={(v) => setC("pan1", v)} /></td></tr>
              <tr><td className={`${td} font-semibold`}>Second Applicant</td><td className={td}><T value={cust.app2} onChange={(v) => setC("app2", v)} /></td></tr>
              <tr><td className={`${td} font-semibold`}>PAN No.</td><td className={td}><T value={cust.pan2} onChange={(v) => setC("pan2", v)} /></td></tr>
              <tr>
                <td className={`${td} font-semibold`}>Contact 1</td><td className={td}><T value={cust.contact1} onChange={(v) => setC("contact1", v)} /></td>
                <td className={`${td} font-semibold`}>Contact 2</td><td className={td}><T value={cust.contact2} onChange={(v) => setC("contact2", v)} /></td>
              </tr>
              <tr>
                <td className={`${td} font-semibold`}>Applicant Mail-ID</td>
                <td className={td} colSpan={3}><T value={cust.email} onChange={(v) => setC("email", v)} /></td>
              </tr>
            </tbody>
          </table>

          {/* Flat details */}
          <div className={`${secBar} mt-4`}>Flat Details</div>
          <table className="w-full border-collapse">
            <tbody>
              <tr>
                <td className={`${td} font-semibold`}>Category</td><td className={td}><T value={cust.category} onChange={(v) => setC("category", v)} placeholder="3.5 BHK / 3T" /></td>
                <td className={`${td} font-semibold`}>Flat No.</td><td className={td}><T value={cust.flatNo} onChange={(v) => setC("flatNo", v)} /></td>
              </tr>
              <tr>
                <td className={`${td} font-semibold`}>Floor</td><td className={td}><T value={cust.floor} onChange={(v) => setC("floor", v)} /></td>
                <td className={`${td} font-semibold`}>Block</td><td className={td}><T value={cust.block} onChange={(v) => setC("block", v)} /></td>
              </tr>
              <tr>
                <td className={`${td} font-semibold`}>Wing</td><td className={td}><T value={cust.wing} onChange={(v) => setC("wing", v)} /></td>
                <td className={`${td} font-semibold`}>Parking No.</td><td className={td}><T value={cust.parking} onChange={(v) => setC("parking", v)} /></td>
              </tr>
              <tr>
                <td className={`${td} font-semibold`}>SBUA (sqft)</td><td className={td}><N value={cust.sbua} onChange={(v) => setC("sbua", v)} /></td>
                <td className={`${td} font-semibold`}>Carpet (sqft)</td><td className={td}><N value={cust.carpet} onChange={(v) => setC("carpet", v)} /></td>
              </tr>
              <tr>
                <td className={`${td} font-semibold`}>Highrise Y/N</td><td className={td}><T value={cust.highrise} onChange={(v) => setC("highrise", v)} /></td>
                <td className={`${td} font-semibold`}>PLC Y/N</td><td className={td}><T value={cust.plc} onChange={(v) => setC("plc", v)} /></td>
              </tr>
            </tbody>
          </table>

          {/* X Basic Sale Price */}
          <div className={`${secBar} mt-4`}>(X) Basic Sale Price</div>
          <table className="w-full border-collapse">
            <thead><tr className="bg-neutral-100">
              <th className={th}>Description</th><th className={th}>Unit</th><th className={th}>Qty</th><th className={th}>Rate/Unit</th><th className={th}>Total Cost</th><th className={th}>GST {propGst}%</th>
            </tr></thead>
            <tbody>
              {rowBlock(basic, setBasic)}
              <tr className="bg-neutral-100 font-bold">
                <td className={td} colSpan={4}>Basic Sale Price</td>
                <td className={`${td} text-right font-mono`} data-testid="cs-basic-total">{money(calc.basicTotal)}</td>
                <td className={`${td} text-right font-mono`}>{money(calc.basicGst)}</td>
              </tr>
            </tbody>
          </table>
          <button onClick={addRow(basic, setBasic)} className="no-print mt-1 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-neutral-500 hover:text-neutral-900"><Plus className="h-3 w-3" /> Add row</button>

          {/* Y Extras */}
          <div className={`${secBar} mt-4`}>(Y) Extras</div>
          <table className="w-full border-collapse">
            <thead><tr className="bg-neutral-100">
              <th className={th}>Description</th><th className={th}>Unit</th><th className={th}>Qty</th><th className={th}>Rate/Unit</th><th className={th}>Total Cost</th><th className={th}>GST {propGst}%</th>
            </tr></thead>
            <tbody>
              {rowBlock(extras, setExtras)}
              <tr className="bg-neutral-100 font-bold">
                <td className={td} colSpan={4}>Total</td>
                <td className={`${td} text-right font-mono`}>{money(calc.extrasTotal)}</td>
                <td className={`${td} text-right font-mono`}>{money(calc.extrasGst)}</td>
              </tr>
            </tbody>
          </table>
          <button onClick={addRow(extras, setExtras)} className="no-print mt-1 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-neutral-500 hover:text-neutral-900"><Plus className="h-3 w-3" /> Add row</button>

          {/* Z Maintenance */}
          <div className={`${secBar} mt-4`}>(Z) Maintenance</div>
          <table className="w-full border-collapse">
            <thead><tr className="bg-neutral-100">
              <th className={th}>Description</th><th className={th}>Unit</th><th className={th}>Qty (sqft)</th><th className={th}>Rate/Month</th><th className={th}>Total Cost</th><th className={th}>GST 18%</th>
            </tr></thead>
            <tbody>
              <tr>
                <td className={td}>Maintenance for <N value={maint.months} onChange={(v) => setMaint({ ...maint, months: v })} className="inline w-12 text-center" /> months</td>
                <td className={`${td} text-center`}>SQFT</td>
                <td className={td}><N value={maint.qty} onChange={(v) => setMaint({ ...maint, qty: v })} /></td>
                <td className={td}><N value={maint.rate} onChange={(v) => setMaint({ ...maint, rate: v })} /></td>
                <td className={`${td} text-right font-mono`}>{money(calc.maintTotal)}</td>
                <td className={`${td} text-right font-mono`}>{money(calc.maintGst)}</td>
              </tr>
            </tbody>
          </table>

          {/* Total */}
          <table className="mt-4 w-full border-collapse">
            <tbody>
              <tr>
                <td className="border border-neutral-400 bg-neutral-900 px-3 py-3 text-sm font-extrabold uppercase tracking-widest text-white">Total Consideration Value (X + Y + Z)</td>
                <td className="border border-neutral-400 px-3 py-3 text-right font-mono text-base font-bold" data-testid="cs-total-cost">{money(calc.totalCost)}</td>
                <td className="border border-neutral-400 px-3 py-3 text-right font-mono text-base font-bold" data-testid="cs-total-gst">+ GST {money(calc.totalGst)}</td>
              </tr>
              <tr>
                <td className="border border-neutral-400 px-3 py-2 text-right text-xs font-bold uppercase" colSpan={2}>Grand Total (incl. GST)</td>
                <td className="border border-neutral-400 bg-neutral-100 px-3 py-2 text-right font-mono text-lg font-extrabold" data-testid="cs-grand">{money(calc.grand)}</td>
              </tr>
            </tbody>
          </table>

          {/* Notes */}
          <div className="mt-5 border border-neutral-400 p-3 text-[11px] leading-relaxed">
            <b>NOTE:</b>
            <ul className="mt-1 list-disc pl-4">
              <li>This is a full and final booking estimate. Agreement of sale will be based on details provided by the customer.</li>
              <li>This form is valid till the agreement is executed. GST as applicable.</li>
              <li>Registration cost as applicable and payable separately.</li>
            </ul>
            <p className="mt-3 text-center font-bold uppercase">All payments in favour of the builder / developer</p>
          </div>
          <div className="mt-8 grid grid-cols-3 gap-4 text-center text-[10px] font-bold uppercase tracking-widest">
            <div className="border-t border-neutral-500 pt-2">Signature of Executive</div>
            <div className="border-t border-neutral-500 pt-2">Authorised Signatory</div>
            <div className="border-t border-neutral-500 pt-2">Customer Signature</div>
          </div>
          <p className="mt-6 text-center font-mono text-[9px] uppercase tracking-widest text-neutral-400">Generated via Real Estate with Ayush · ayush-realty-hub.emergent.host</p>
        </div>

        <p className="no-print mt-4 font-mono text-[10px] text-muted-foreground">Tip: edit any cell directly. Click &ldquo;Download PDF&rdquo;, share your details, and a ready-to-share PDF lands in your downloads.</p>
      </div>
    </div>
  );
}
